import React from "react";

import { fetchAuditEvents } from "../api/accessAdminClient";

type AuditEvent = {
  id: string;
  eventType: string;
  createdAt: string;
  tenantId?: string | null;
  userId?: string | null;
  reason?: string | null;
  metadata?: Record<string, unknown> | null;
};

export function AuditSettingsScreen(): JSX.Element {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tenantId, setTenantId] = React.useState("");
  const [events, setEvents] = React.useState<AuditEvent[]>([]);

  const loadEvents = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAuditEvents(tenantId || undefined);
      setEvents((response.events ?? []) as AuditEvent[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load audit events");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  React.useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  if (loading) {
    return <div className="p-6 text-sm text-slate-600">Loading audit events…</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Audit log</h2>
        <p className="text-sm text-slate-600">Review access-related audit events.</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            className="input"
            placeholder="Tenant ID (optional)"
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
          />
          <button type="button" className="button button-secondary" onClick={loadEvents}>
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-3">
          {events.length === 0 ? (
            <div className="text-sm text-slate-500">No audit events found.</div>
          ) : (
            events.map((event) => (
              <div key={event.id} className="rounded border border-slate-100 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">{event.eventType}</div>
                  <div className="text-xs text-slate-500">{new Date(event.createdAt).toLocaleString()}</div>
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  Tenant: {event.tenantId ?? "—"} · User: {event.userId ?? "—"}
                </div>
                {event.reason ? <div className="mt-2 text-xs text-slate-600">Reason: {event.reason}</div> : null}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
